// src/services/supportFormat.js
// Pure helpers for the ticket screens (labels, dates, customer numbers), kept
// free of Firebase imports so they can be unit-tested on their own.

export const STATUS_LABELS = {
  open: 'Open',
  in_progress: 'In progress',
  waiting_on_student: 'Waiting on you',
  on_hold: 'On hold',
  escalated: 'Escalated',
  resolved: 'Resolved',
  closed: 'Closed',
};

// Badge colours: [text, background]. Unknown statuses fall back to grey.
const STATUS_COLOURS = {
  open: ['#b45309', 'rgba(245,158,11,0.16)'],
  in_progress: ['#1d4ed8', 'rgba(59,130,246,0.14)'],
  waiting_on_student: ['#c2410c', 'rgba(249,115,22,0.16)'],
  on_hold: ['#57527e', 'rgba(107,104,146,0.16)'],
  escalated: ['#b91c1c', 'rgba(220,38,38,0.14)'],
  resolved: ['#15803d', 'rgba(22,163,74,0.14)'],
  closed: ['#57527e', 'rgba(30,27,75,0.08)'],
};

export function statusLabel(status) {
  return STATUS_LABELS[status] || 'Open';
}

export function statusColours(status) {
  return STATUS_COLOURS[status] || STATUS_COLOURS.closed;
}

// Only finished tickets can be relaunched (the server enforces it too).
export function canRelaunch(status) {
  return status === 'resolved' || status === 'closed';
}

// Accepts an ISO string, a millisecond number, or a Firestore-style
// { seconds } / { _seconds } timestamp. Returns '' for anything unreadable.
export function formatTicketDate(value) {
  if (value === null || value === undefined || value === '') return '';
  let ms;
  if (typeof value === 'object') {
    const secs = value.seconds ?? value._seconds;
    ms = typeof secs === 'number' ? secs * 1000 : NaN;
  } else if (typeof value === 'number') {
    ms = value;
  } else {
    ms = new Date(value).getTime();
  }
  if (Number.isNaN(ms)) return '';
  return new Date(ms).toLocaleDateString([], { day: 'numeric', month: 'short', year: 'numeric' });
}

// Newest activity first; tickets without a date sink to the bottom.
export function sortTickets(tickets) {
  const t = (x) => {
    const v = x.updatedAt || x.createdAt;
    const ms = typeof v === 'object' && v ? (v.seconds ?? v._seconds) * 1000 : new Date(v).getTime();
    return Number.isNaN(ms) ? 0 : ms;
  };
  return [...tickets].sort((a, b) => t(b) - t(a));
}

// "ELP-123456" style references are shown as given; just guard against blanks.
export function ticketReference(ticket) {
  return (ticket && ticket.id) || '';
}

// A paused AI chat is signalled by a 503 {paused:true}.
export function isPausedError(status, data) {
  return status === 503 && !!(data && data.paused);
}

export function formatCustomerNumber(customer) {
  return (customer && customer.customerNumber) || '';
}

export const AI_PAUSED_NOTE = 'AI chat is paused, contact the team';
